import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  Signal,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormGroup, FormsModule, NgForm, ReactiveFormsModule, Validators } from '@angular/forms';
import { ClarityModule, ClrForm, ClrSidePanel } from '@clr/angular';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, firstValueFrom, map, of, startWith, switchMap } from 'rxjs';
import { Table } from 'src/app/models/ui/table';
import { GetByCallbackPipe } from 'src/app/pipes/get-by-callback.pipe';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { BasicStoreService } from 'src/app/services/BasicStore/basic-store.service';
import { HilfeBeschreibungModalComponent } from '../../_components/_modals/hilfebeschreibungmodal/hilfebeschreibungmodal.component';
import { WartungsBaumNode, WartungsPlanNode } from '../../_components/_overlays/wartungsverwaltung/wartung.types';
import { ArtGroup, GroupedArtNodes, ProtokollModel, WartungProtokollNode } from './wartung-protokoll.types';
import { ZaehlerStore } from '../../_components/_tabs/zaehlertab/zaehler.store.service';
import { VerknüpfungsTyp } from '../../_components/_overlays/zaehlerverwaltung/zaehler.enums';
import { ZaehlertabComponent } from '../../_components/_tabs/zaehlertab/zaehlertab.component';
import { DetailViewAnsicht, DetailviewComponent } from '../../_components/detailview/detailview.component';
import { WartungMediaApiFuncService } from 'src/app/services/MediaApiFunc/wartung-media-api-func.service';
import { MediaApiFunc } from 'src/app/services/MediaApiFunc/abstract-media-api-func';
import { CachedMediaStore } from 'src/app/shared/stores/media-cached.store';
import { PreviewThumbnailsComponent } from '../../_components/_shared/preview-thumbnails/preview-thumbnails.component';
import {
  MediaViewModalComponent,
  TitleAndFileNames,
} from '../../_components/_shared/mediaviewmodal/mediaviewmodal.component';
import { GroupedProtokollNodes, NgFormModel } from './wartung-protokoll.types';
import { FileAPIModel } from '../../_components/_shared/preview-thumbnails/preview-thumbnail.type';
import { WartungApiService } from 'src/app/services/APIService/wartung.api.service';
import { mapObjectAsync } from 'src/app/misc/functions';
import { ConfirmService } from 'src/app/services/confirm.service';
import { BaseItemsWrapperComponent } from '../../_components/_modals/baseitems-wrapper/baseitems-wrapper.component';
import { DatalistInputComponent } from '../../_components/_shared/datalist-input/datalist-input.component';

@Component({
  selector: 'app-wartungdialog',
  templateUrl: './wartungdialog.component.html',
  styleUrls: ['./wartungdialog.component.scss'],
  imports: [
    CommonModule,
    ClarityModule,
    MediaViewModalComponent,
    BaseItemsWrapperComponent,
    HilfeBeschreibungModalComponent,
    GetByCallbackPipe,
    BackendLocaleDatePipe,
    MrTranslatePipe,
    FormsModule,
    ReactiveFormsModule,
    DetailviewComponent,
    ZaehlertabComponent,
    PreviewThumbnailsComponent,
    DatalistInputComponent,
  ],
  providers: [ZaehlerStore, { provide: MediaApiFunc, useClass: WartungMediaApiFuncService }, CachedMediaStore],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WartungDialogComponent implements OnInit {
  private readonly apiService = inject(APIService);
  private readonly store = inject(BasicStoreService);
  private readonly toastr = inject(ToastrService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  protected readonly zaehlerStore = inject(ZaehlerStore);
  private readonly api = inject(WartungApiService);
  private readonly confirmService = inject(ConfirmService);
  protected readonly cachedMedia = inject(CachedMediaStore);
  protected readonly media = inject(WartungMediaApiFuncService);
  protected readonly localeDate = inject(BackendLocaleDatePipe);
  public closedialog = new Subject<boolean>();
  private dataRefresh: Subject<void>;

  private readonly preview = viewChild(PreviewThumbnailsComponent);
  protected readonly wartungForm = viewChild('wartungForm', { read: NgForm });
  protected readonly clrForm = viewChild('wartungForm', { read: ClrForm });
  private readonly zaehlertab = viewChild(ZaehlertabComponent);

  protected wartung$: Observable<GroupedProtokollNodes>;
  protected maengel$: Observable<Table>;

  protected bemerkungen$: Observable<string[]>;
  protected meh$: Observable<string[]>;
  protected readonly requiredFK = this.apiService.getTypOption(() => this.objekt().OTYPID, 'Opt_I_Fehlerklasse');

  protected rangeFK: number[] = [];
  protected readonly wartungsdate = signal(BackendLocaleDatePipe.now); // ! heute passt nicht
  protected readonly bemerkung = signal<string>(null);

  protected readonly objekt = signal<WartungsPlanNode>(null);

  protected readonly isReadOnly = signal(false);
  protected readonly isNew = signal(false);
  protected readonly assignedZaehlerName = this.zaehlerStore.assignedZaehlerName;
  protected readonly isDetailPanelOpened = signal(false);
  protected readonly isBaseModalOpen = signal(false);
  protected readonly isHelperModalOpen = signal(false);
  protected readonly openedHelperId = signal<number>(null);
  protected readonly wartungsMedia = signal<TitleAndFileNames[]>([]);
  protected readonly isPreviewModalOpen = signal(false);
  protected readonly mediaRow = signal<WartungProtokollNode | WartungsPlanNode>(null);
  protected readonly delFiles = signal<string[]>([]);

  protected readonly toBeSaved = computed(() => this.preview()?.unsaved().length || this.delFiles().length);

  protected readonly showHilfeColumn = signal(false);
  protected isAllSelected = (rows: GroupedArtNodes) => [...rows.values()].every(row => row.artTyp == 0 || row.iO);

  public set data(dataIn: any) {
    const objekt = dataIn.objekt;
    const readonly = dataIn.mode === 'readonly';
    const neu = dataIn.mode === 'neu';

    this.dataRefresh = dataIn.dataRefresh;

    this.objekt.set(objekt);
    this.isReadOnly.set(readonly);
    this.isNew.set(neu);

    this.zaehlerStore.verknüpfung.set(VerknüpfungsTyp.Wartungszähler);
    this.zaehlerStore.objekt.set({
      OPRUEFID: this.objekt().OPRUEFID,
      OSTAMMID: this.objekt().OSTAMMID,
      mID: this.objekt().mID ?? this.objekt().archiv_mID,
    });
    this.zaehlerStore.isReadOnly.set(readonly);
    this.zaehlerStore.isNew.set(neu);

    this.getWartung();

    if (!neu) {
      this.wartungsdate.set(objekt.letzteDatum);
      this.bemerkung.set(objekt.bemerkung);
    }

    if (!readonly) {
      this.bemerkungen$ = this.api.getWartungsBemerkungenList();
      this.meh$ = this.apiService.getMaengelEinheiten();
    }
  }

  constructor() {
    const activePreviewEffect = effect(() => {
      if (this.isReadOnly()) {
        activePreviewEffect.destroy();
        return;
      }

      const open = this.isPreviewModalOpen();
      untracked(() => {
        const selected = this.mediaRow();
        if (!selected) return;

        const id = this.getCachedID(selected);
        if (open) {
          if (this.isNew()) this.cachedMedia.getFromCache(id, this.preview());
          else this.cachedMedia.getBilder(id, selected.RevBild, this.preview());
        } else if (this.isNew()) {
          this.cachedMedia.setInCache(id, this.preview());
        }
      });
    });
  }

  protected exportZaehlerExcel() {
    this.zaehlertab().excelExport();
  }

  private getWartung() {
    this.wartung$ = this.dataRefresh.pipe(
      startWith(null),
      switchMap(() =>
        this.api.getWartungNachMaske(this.objekt().mID, this.isNew() ? -1 : this.objekt().wID, this.isReadOnly())
      ),
      map(rows =>
        this.isReadOnly() ?
          (rows as unknown as WartungProtokollNode[])
        : rows.map<WartungProtokollNode>(row => {
            const zuordnungen = (row.zuordnungen || null)?.split('&.&').reduce(
              ({ arbeiten, FKs }, zuordnung) => {
                // split('&.&') - verschiedene Gruppen von arbeitID, arbeitBez u. Priorität
                const [id, bez, fk] = zuordnung.split('|.|');
                arbeiten[bez] = +id;
                FKs[+id] = +fk;
                return { arbeiten, FKs };
              },
              { arbeiten: {}, FKs: {} }
            );
            return { ...row, zuordnungen };
          })
      ),
      map(
        nodes =>
          nodes.reduce<{ groups: GroupedProtokollNodes; letzteHeaderID: number }>(
            ({ groups, letzteHeaderID }, node) => {
              //! Erseztz gleiche art.ID bei fehlgeschlagen gespeicherten Einträgen auf letzte / max ID
              const id = node.artID;
              const key = `${node.rSort ?? node.ID}-${id}`;

              if (node.artTyp == 0) {
                const { HelpBeschreibung, HelpBildBase64, FileTyp } = node;
                const headerKey = `${node.rSort ?? node.ID}-${node.artName}`;
                const children = new Map();
                if (!this.isReadOnly()) children.set(id, { ...node, key });
                groups.set(id, {
                  key: headerKey,
                  children,
                  name: node.artName,
                  helper: { HelpBeschreibung, HelpBildBase64, FileTyp },
                });
                letzteHeaderID = id;
              } else {
                if (letzteHeaderID === null) {
                  groups.set(0, { children: new Map(), key: '0' });
                  letzteHeaderID = 0;
                }
                if (node.HelpBeschreibung || node.FileTyp) this.showHilfeColumn.set(true);

                groups.get(letzteHeaderID).children.set(id, { ...node, FK: node.FK ?? null, key });
              }

              return { groups, letzteHeaderID };
            },
            { groups: new Map(), letzteHeaderID: null }
          ).groups
      )
    );
  }

  ngOnInit() {
    const { einstellung: { optMINNumFK = 0, optMAXNumFK = 4 } = {} } = this.store.getComponentStore('einstellung');
    for (let fk = optMINNumFK; fk <= optMAXNumFK; fk++) {
      this.rangeFK.push(fk);
    }
  }

  protected async createArbeitAndFindFK(row: WartungProtokollNode) {
    const { zuordnungen: { FKs, arbeiten } = {}, arbeitName } = row;
    if (!arbeitName) {
      row.arbeitID = null;
      return;
    }

    const arbeitID = arbeiten?.[arbeitName];

    if (arbeitID) {
      row.arbeitID = arbeitID;
      const fk = FKs?.[arbeitID];
      if (this.rangeFK.includes(fk)) row.FK = fk;
    } else {
      row.arbeitID = await firstValueFrom(this.api.getWartungsArbeitID(arbeitName, this.objekt().OTYPID));
      if (!row.arbeitID) {
        this.toastr.error(this.mrTranslate.transform('Wartungsarbeit konnte nicht gefunden oder erstellt werden'));
        return;
      }

      const sendObj: WartungsBaumNode = {
        artID: row.artID,
        arbeitID: row.arbeitID,
        Priorität: row.FK || 0,
      };
      const { success, error } = await firstValueFrom(this.api.setWartungsBaumNode(this.objekt().OTYPID, sendObj));

      if (success) this.toastr.success(this.mrTranslate.transform('Wartungsverknüpfung erfolgreich gespeichert'));
      else this.toastr.error(error, this.mrTranslate.transform('Wartungsverknüpfung konnte nicht gespeichert werden'));
    }
  }

  /**
   * @description Ergänzt formValue mit Base64-Media
   * @async
   */
  private async appendMediaToRows(formValue: NgFormModel): Promise<NgFormModel> {
    return mapObjectAsync(formValue, async ([headerID, headerBlock]) => [
      headerID,
      await mapObjectAsync(headerBlock, async ([artID, artGroup]) => [
        artID,
        { ...artGroup, files: await this.cachedMedia.packFilesFromCache(+artID.split('-')[1]) },
      ]),
    ]);
  }

  private async appendMediaToWartung() {
    return await this.cachedMedia.packFilesFromCache(this.adjustWartungsPlanID(this.objekt().ID));
  }

  protected async saveIfValid() {
    if (this.wartungForm().invalid) {
      this.clrForm().markAsTouched();
      return;
    }
    let form: NgFormModel = this.wartungForm().value;

    let sendObj: ProtokollModel = {
      wID: this.objekt().wID,
      opruefid: this.objekt().OPRUEFID,
      ostammid: this.objekt().OSTAMMID,
      otypid: this.objekt().OTYPID,
      mID: this.objekt().mID,
      zID: this.objekt().ID,
      maske: this.objekt().maske,
      personal: this.objekt().verantwortlich,
      datum: this.wartungsdate() || BackendLocaleDatePipe.now,
      bemerkung: this.bemerkung(),
      files: null,
      form: this.wartungForm().dirty ? form : null,
    };

    if (this.isNew()) {
      const media = await Promise.all([this.appendMediaToRows(form), this.appendMediaToWartung()]);
      sendObj = { ...sendObj, wID: null, opruefid: null, form: media[0], files: media[1] };
    }

    const { success, wID, pruefID, mediaStr } = await firstValueFrom(this.api.setWartung(sendObj));

    if (success) {
      this.objekt().wID = wID;
      this.objekt().OPRUEFID = pruefID;
      if (this.isNew()) {
        this.objekt().RevBild = mediaStr;
        this.cachedMedia.resetCache();
        this.isNew.set(false);
        this.zaehlerStore.isNew.set(false);
      }
      this.wartungForm().form.markAsPristine();

      this.toastr.success(
        this.mrTranslate.transform('Erfolgreich gespeichert!'),
        this.mrTranslate.transform('Wartung')
      );
      this.dataRefresh.next();

      if (this.assignedZaehlerName()) {
        const success = await this.zaehlerStore.onSave(pruefID);
        this.zaehlerStore.toBeSaved.set(!success);
      }
    } else
      this.toastr.error(this.mrTranslate.transform('Etwas ist schief gelaufen'), this.mrTranslate.transform('Wartung'));
  }

  protected addDelFile(name: unknown) {
    if (this.isNew()) return;
    this.delFiles.update(files => (files.push(name as string), [...files]));
  }

  protected async saveMedia() {
    const selected = this.mediaRow();
    const isPassbild = 'wID' in selected;
    const sendObj: FileAPIModel = {
      files: await this.preview().packUnsavedFiles(),
      savedFiles: this.preview().savedNames(),
      deletedFiles: this.delFiles(),
    };

    const { success, error, mediaStr } = await firstValueFrom(
      this.api.setWartungsImage(isPassbild, this.objekt().wID, selected.ID, sendObj)
    );

    if (success) {
      selected.RevBild = mediaStr;
      this.cachedMedia.reloadAfterSaved(this.getCachedID(selected), this.preview(), this.delFiles());
      this.closePreview();
      this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert!'), this.mrTranslate.transform('Media'));
    } else {
      console.error('saveMedia: ', error);
      this.toastr.error(this.mrTranslate.transform('Ein Fehler ist aufgetreten'));
    }
  }

  protected setWartungMedia(node: WartungProtokollNode | WartungsPlanNode) {
    const filepath = node.RevBild;
    const isPassbild = 'wID' in node;
    const media =
      isPassbild ?
        this.media.parseMediaPath(filepath)
      : this.media.parseMediaPathWithTitle({ title: node.artName, filepath });
    this.wartungsMedia.set(media);
  }

  protected closePreview() {
    // * After Saving or After Schliessen == modal.close() or After Abbrechen if toBeSaved()
    this.mediaRow.set(null);
    this.delFiles.set([]);
    this.isPreviewModalOpen.set(false);
  }

  private getCachedID(selected: WartungsPlanNode | WartungProtokollNode) {
    const isPassbild = 'wID' in selected;
    return isPassbild ? this.adjustWartungsPlanID(selected.ID) : selected.artID;
  }

  private adjustWartungsPlanID(zID: number) {
    return zID + 1_000_000;
  }

  private split50 = false;
  protected handleDetailviewEnlarge($event: DetailViewAnsicht, detailPanel: ClrSidePanel) {
    switch ($event.mode) {
      case 'full':
        detailPanel.size =
          $event.val ? 'full-screen'
          : this.split50 ? 'lg'
          : 'md';
        break;
      case 'half':
        this.split50 = $event.val;
        if (detailPanel.size !== 'full-screen') detailPanel.size = this.split50 ? 'lg' : 'md';
        break;
      case 'close':
        detailPanel.close();
        break;
    }
  }

  protected checkAll(select: boolean, headerGroup: FormGroup) {
    Object.values(headerGroup.controls).forEach((control: FormGroup) => control.controls['iO']?.setValue(select));
    headerGroup.markAsDirty();
  }

  protected setValidations(iO: boolean, artGroup: FormGroup) {
    if (!artGroup) return;
    const { massnahmen: arbCtrl, fk: fkCtrl } = artGroup.controls;
    arbCtrl.setValidators(iO ? null : Validators.required);
    arbCtrl.updateValueAndValidity();
    if (this.requiredFK()) {
      fkCtrl.setValidators(iO ? null : Validators.required);
      fkCtrl.updateValueAndValidity();
    }
  }

  protected updateMEH(list: string[]) {
    this.meh$ = of(list);
  }

  protected async close() {
    const saved = !(this.zaehlerStore.toBeSaved() || this.wartungForm()?.dirty || this.cachedMedia.hasUnsaved());

    const ok = () =>
      this.confirmService.warning(this.mrTranslate.transform('Einträge sind nicht gespeichert! Fortfahren?'));
    if (saved || (await ok())) this.closedialog.next(true);
  }
}
